<template>
  <div class="result-card-wrapper">
    <!-- Survey Modal -->
    
    <!-- Survey Modal - Pass toolBranchId directly -->
    <SurveyModal 
      v-if="showSurveyModal"
      :viewCount="resultViewCount"
      :recentBranchUsedId="toolBranchId"
      @submit-survey="handleSubmitSurvey"
      @remind-later="handleRemindLater"
    />
    
    <div class="result-card-container">
      <div class="result-card-modern">
        <!-- All your existing card content -->
        <div class="card-header-section">
          <div class="rank-badge-modern">
            <h4 class="rank-number">{{ treatmentName }}</h4>  
          </div>
          <div class="confidence-badge-modern" :class="getConfidenceClass()">
            <span>{{ treatmentPercent }}% Consensus</span>
          </div>
        </div>

        <div class="treatment-section">
          <a
            v-if="toolBranchId > 0"
            href="#"
            @click.prevent="goToBranch()"
            class="assessment-link"
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M13 10V3L4 14h7v7l9-11h-7z"/>
            </svg>
            Go to this differential diagnosis
          </a>
        </div>

        <div v-if="filteredQuestions.length > 0" class="key-decisions-section">
          <div class="section-title">
            Key Decision Criteria
          </div>
          <ul class="decisions-list">
            <li v-for="(question, index) in filteredQuestions" :key="'decision-' + index">
              <span class="decision-marker"></span>
              {{ question }}
            </li>
          </ul>
        </div>

        <div class="links-section">
          <div class="section-title">
            <span>References</span>
          </div>
          <div class="links-list">
            <a 
              v-for="(publication, index) in publications" 
              :key="'pub-' + index"
              class="link-item" 
              :href="publication.link" 
              target="_blank"
            >
              <span>{{ publication.name }}</span>
              <img 
                src="../../assets/externallink.png" 
                alt="External link icon"
                class="link-icon"
                style="width: 18px; height: 18px;"
              />
            </a>
            
            <a 
              v-if="smpcLink.length > 0"
              class="link-item" 
              :href="smpcLink" 
              target="_blank"
            >
              <span>SMPC</span>
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
              </svg>
            </a>
          </div>
        </div>

        <div v-if="treatmentNotes.length > 0" class="additional-info-section">
          <div class="section-title">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
            Additional Information
          </div>
          <ul class="info-list">
            <li v-for="(note, index) in treatmentNotes" :key="'note-' + index">
              {{ note }}
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>



<script lang="ts">
import {defineComponent, ref, reactive, onMounted, watch, computed} from 'vue';
import { Publication } from '@/entities/questionaire/Links';
import CircleComponent from './CircleComponent.vue';
import SurveyModal from './SurveyModal.vue';
import store from "@/store/index";
import { toastService } from '@/services/toastService';
import { HttpHandler } from '@/http/HttpHandler';

export default defineComponent({
  components: {
    CircleComponent,
    SurveyModal,
  },
  props: {
    resultIndex: {
      type: Number,
      required: true
    },
    toolBranchId: {
      type: Number,
      required: false
    },
    treatmentName: {
      type: String,
      required: true
    },
    treatmentPercent: {
      type: Number,
      required: true
    },
    treatmentNotes: {
      type: Array,
      default: () => [],
      required: true
    },
    smpcLink: {
      type: String,
      required: true
    },
    publications: {
      type: Array,
      default: () => [],
      required: true
    },
  },
  computed: {
    filteredQuestions(): string[] {
      const questions: string[] = [];
      const visibleQuestions = store.getters['localQuestionsTreatment/getVisibleQuestions'];
      
      for (let i = 0; i < visibleQuestions.length; i++) {
        const currentQuestion = visibleQuestions[i];
        const previousQuestion = i > 0 ? visibleQuestions[i - 1] : null;
        
        if (currentQuestion.treatment > 0 && currentQuestion.keyDecision != '' && 
            (!previousQuestion || (currentQuestion.keyDecision.length > 0 && currentQuestion.treatment !== 0))) {
          questions.push(currentQuestion.keyDecision);
        }
      }
      
      return questions;
    }
  },
  methods: {
    getRankLabel(): string {
      if (this.resultIndex === 1) return "Top Choice";
      if (this.resultIndex === 2) return "Alternative";
      if (this.resultIndex === 3) return "Third Option";
      return "Option";
    },
    getConfidenceClass(): string {
      if (this.treatmentPercent >= 80) return "confidence-high";
      if (this.treatmentPercent >= 60) return "confidence-medium";
      return "confidence-low";
    },
    goToBranch() {
      console.log("Navigating from Result Card to branch with ID:", this.toolBranchId);
      this.$events.emit("goToBranch", this.toolBranchId);
    }
  },
  setup(props) {
    const consensusStrength = 'Panel consensus strength';
    const showSurveyModal = ref(false);
    const resultViewCount = ref(0);
    const RESULT_VIEW_THRESHOLD = 4;
    
    // Debug info
    const debugInfo = reactive({
      fillSurvey: false,
      viewCount: 0,
      
      lastResultHash: '',
      currentBranchId: props.toolBranchId
    });
    
    // Computed property to watch for result changes
    const resultSignature = computed(() => {
      return `${props.treatmentName}-${props.treatmentPercent}`;
    });
    
    // Check if survey should be shown
    const checkAndShowSurvey = () => {
      const fillSurveyStr = localStorage.getItem('fillSurvey');
      const fillSurvey = fillSurveyStr ? JSON.parse(fillSurveyStr) : false;
      //const surveyCompleted = localStorage.getItem('survey_completed') === 'true';
      const currentCount = parseInt(localStorage.getItem('result_view_count') || '0');
      
      // Get the last result signature to detect if this is a new result
      const lastResultSignature = sessionStorage.getItem('last_result_signature');
      const currentSignature = resultSignature.value;
      
      let newCount = currentCount;
      
      // Increment if this is a different result OR if there's no last signature (first time)
      if (!lastResultSignature || lastResultSignature !== currentSignature) {
        newCount = currentCount +1;
        localStorage.setItem('result_view_count', newCount.toString());
        sessionStorage.setItem('last_result_signature', currentSignature);
        // console.log('📊 New/Different result detected! Count:', newCount);
        // console.log('   Previous:', lastResultSignature);
        // console.log('   Current:', currentSignature);
      } else {
        console.log('🔄 Same result, not incrementing. Count remains:', currentCount);
      }
      
      // Update debug info
      debugInfo.fillSurvey = fillSurvey;
      debugInfo.viewCount = newCount;
   
      debugInfo.lastResultHash = currentSignature;
      debugInfo.currentBranchId = props.toolBranchId;
      
      resultViewCount.value = newCount;
      
    //   console.group('🔍 Survey Check');
    //   console.log('Result Index:', props.resultIndex);
    //   console.log('Total result views:', newCount);
    //   console.log('Tool Branch ID:', props.toolBranchId);
    //   console.log('Fill survey required:', fillSurvey);
    //  // console.log('Survey completed:', surveyCompleted);
    //   console.log('Threshold:', RESULT_VIEW_THRESHOLD);
    //   console.log('Should show:', fillSurvey && newCount > RESULT_VIEW_THRESHOLD);
    //   console.groupEnd();
      
      // Show survey if conditions are met
      if (fillSurvey && newCount >= RESULT_VIEW_THRESHOLD) {
        console.log('✅ Showing survey modal');
        showSurveyModal.value = true;
      } else {
        console.log('❌ Not showing survey modal');
        if (!fillSurvey) console.log('   Reason: fillSurvey is false');
        if (newCount < RESULT_VIEW_THRESHOLD) console.log(`   Reason: Need ${RESULT_VIEW_THRESHOLD - newCount} more views`);
      }
    };
    
    // Handle survey submission
    const handleSubmitSurvey = async (surveyData: any) => {
      console.log('📝 handleSubmitSurvey called with data:', surveyData);
      
      try {
        const httpHandler = new HttpHandler(store);
        
        // Prepare the survey payload with the branchId from surveyData (passed from modal)
        const payload = {
          recentBranchUsedId: surveyData.recentBranchUsedId,
          wasHelpful: surveyData.wasHelpful,
          wouldUseAgain: surveyData.wouldUseAgain,
          recommendationScore: surveyData.recommendationScore,
          comments: surveyData.comments
        };
        
        console.log('🔄 Sending survey to API:', payload);
        console.log('   Branch ID:', payload.recentBranchUsedId);
        
        // Call the actual API
        await httpHandler.submitSurvey(payload);
        
        console.log('✅ API call completed successfully');
        
        // Mark survey as completed
        localStorage.setItem('fillsurvey', 'false');
        showSurveyModal.value = false;
        
        // Show success message
        toastService.showSuccess('Thank you for your feedback! Your input helps us improve ProxyMed.');
        
        console.log('✅ Survey submitted successfully');
        
      } catch (error: any) {
        console.error('❌ Error submitting survey:', error);
        console.error('Error response:', error.response);
        console.error('Error data:', error.response?.data);
        
        // Show error message with details
        const errorMessage = error?.response?.data?.message || 
                            error?.response?.data?.error ||
                            error?.message ||
                            'Failed to submit survey. Please try again later.';
        toastService.showError(errorMessage);
      }
    };
    
    const handleRemindLater = () => {
      console.log('⏰ User clicked Remind Later');
      showSurveyModal.value = false;
      // Reset count to remind after 3 more views
      localStorage.setItem('result_view_count', '0');
      debugInfo.viewCount = 0;
      
      toastService.showError('We\'ll remind you after your next few uses.');
    };
    
    // Watch for changes in result data (for when user edits answers)
    watch(resultSignature, (newValue, oldValue) => {
  
      
      // Only check if this is the first result card and the signature actually changed
      if (props.resultIndex === 1 && oldValue && newValue !== oldValue) {
        checkAndShowSurvey();
      }
    });
    
    onMounted(() => {
      console.log('🎬 ResultCard mounted, resultIndex:', props.resultIndex);
      // Only check for first result card to avoid showing modal multiple times
      if (props.resultIndex === 1) {
        checkAndShowSurvey();
      }
    });
    
    return {
      consensusStrength,
      showSurveyModal,
      resultViewCount,
      debugInfo,
      handleSubmitSurvey,
      handleRemindLater,
    };
  },
});
</script>


<style scoped lang="scss">
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

.result-card-wrapper {
  position: relative;
}

.result-card-container {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  padding: 1rem;
}

.result-card-modern {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 30px rgba(47, 69, 165, 0.12);
  }
}

/* Header Section */
.card-header-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid #e2e8f0;
  margin-bottom: 1.5rem;
}

.rank-badge-modern {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.rank-number {
  font-size: 1.25rem;
  font-weight: 800;
  background: linear-gradient(135deg, #2f45a5, #667bf2);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  line-height: 1;
}

.rank-label {
  font-size: 0.75rem;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
}

.confidence-badge-modern {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 12px;
  font-weight: 700;
  font-size: 0.9rem;

  svg {
    width: 18px;
    height: 18px;
  }

  &.confidence-high {
    background: linear-gradient(135deg, rgba(16, 185, 129, 0.1), rgba(52, 211, 153, 0.1));
    color: #059669;
    border: 1px solid rgba(16, 185, 129, 0.2);
  }

  &.confidence-medium {
    background: linear-gradient(135deg, rgba(245, 158, 11, 0.1), rgba(251, 191, 36, 0.1));
    color: #d97706;
    border: 1px solid rgba(245, 158, 11, 0.2);
  }

  &.confidence-low {
    background: linear-gradient(135deg, rgba(239, 68, 68, 0.1), rgba(248, 113, 113, 0.1));
    color: #dc2626;
    border: 1px solid rgba(239, 68, 68, 0.2);
  }
}

/* Treatment Section */
.treatment-section {
  margin-bottom: 1.5rem;
}

.treatment-title {
  font-size: 1.2rem ;
  font-weight: 500;
  color: #1e293b;
  margin-bottom: 0.75rem;
  line-height: 1.3;
}
@media (max-width: 600px) {
.treatment-title {
  font-size: 1rem;
}
}
.assessment-link {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: linear-gradient(135deg, rgba(102, 123, 242, 0.1), rgba(47, 69, 165, 0.05));
  color: #2f45a5;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;
  border: 1px solid rgba(102, 123, 242, 0.2);

  svg {
    width: 16px;
    height: 16px;
  }

  &:hover {
    background: linear-gradient(135deg, rgba(102, 123, 242, 0.15), rgba(47, 69, 165, 0.1));
    border-color: rgba(102, 123, 242, 0.3);
    transform: translateX(4px);
  }
}

/* Links Section */
.links-section {
  background: linear-gradient(135deg, rgba(248, 250, 252, 0.8), rgba(241, 245, 255, 0.6));
  border-radius: 12px;
  padding: 1.25rem;
  margin-bottom: 1.5rem;
}

.links-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 0.75rem;
  font-size: 0.95rem;

  svg {
    width: 18px;
    height: 18px;
    color: #667bf2;
  }
}

.links-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.link-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.625rem 0.875rem;
  background: white;
  border-radius: 8px;
  color: #2f45a5;
  text-decoration: none;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  border: 1px solid transparent;

  svg {
    width: 14px;
    height: 14px;
  }

  &:hover {
    border-color: rgba(102, 123, 242, 0.3);
    background: rgba(248, 250, 252, 1);
    transform: translateX(4px);
  }
}

/* Consensus Section */
.consensus-section {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1.5rem;
  background: linear-gradient(135deg, rgba(248, 250, 252, 0.8), rgba(241, 245, 255, 0.6));
  border-radius: 12px;
  margin-bottom: 1.5rem;
}

.consensus-visual {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.consensus-label {
  font-weight: 600;
  color: #475569;
  font-size: 0.95rem;
}

/* Additional Info Section */
.additional-info-section {
  padding: 1.25rem;
  background: rgba(248, 250, 252, 0.5);
  border-radius: 12px;
  margin-bottom: 1.5rem;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 0.75rem;
  font-size: 1rem;

  svg {
    width: 20px;
    height: 20px;
    color: #667bf2;
  }
}

.info-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.info-list li {
  padding-left: 1.5rem;
  position: relative;
  color: #475569;
  line-height: 1.6;
  font-size: 0.95rem;

  &::before {
    content: '•';
    position: absolute;
    left: 0;
    color: #667bf2;
    font-weight: 700;
    font-size: 1.25rem;
  }
}

/* Key Decisions Section */
.key-decisions-section {
  padding: 1.25rem;
  background: linear-gradient(135deg, rgba(102, 123, 242, 0.05), rgba(47, 69, 165, 0.02));
  border-radius: 12px;
  border: 1px solid rgba(102, 123, 242, 0.1);
  margin-bottom: 1rem;
}

.decisions-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  max-height: 300px;
  overflow-y: auto;
  padding-right: 0.5rem;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #cbd5e1;
    border-radius: 2px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: #94a3b8;
  }
}

.decisions-list li {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  color: #475569;
  line-height: 1.6;
  font-size: 0.95rem;
}

.decision-marker {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: linear-gradient(135deg, #10b981, #34d399);
  margin-top: 0.5rem;
  flex-shrink: 0;
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .result-card-container {
    padding: 0.5rem 0;
  }

  .result-card-modern {
    padding: 1.5rem;
    border: 2px solid #10b981;
    border-top: 5px solid #10b981;
    box-shadow: 0 6px 24px rgba(16, 185, 129, 0.15);
  }

  .card-header-section {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }

  .rank-number {
    font-size: 1rem;
    line-height: 1.5;
  }

  .confidence-badge-modern {
    width: 100%;
    justify-content: center;
    padding: 0.625rem 1rem;
    font-size: 1rem;
  }

  .links-section,
  .consensus-section,
  .additional-info-section,
  .key-decisions-section {
    padding: 1rem;
  }

  .assessment-link {
    width: 100%;
    justify-content: center;
  }

  .consensus-visual {
    flex-direction: column;
    text-align: center;
  }

  .decisions-list {
    max-height: 200px;
  }
}

@media (max-width: 480px) {
  .result-card-modern {
    padding: 1.25rem 1rem;
    border-radius: 12px;
    border: 2px solid #10b981;
    border-top: 5px solid #10b981;
  }

  .card-header-section {
    padding-bottom: 1rem;
    margin-bottom: 1rem;
  }

  .rank-number {
    font-size: 0.95rem;
    line-height: 1.5;
  }

  .rank-label {
    font-size: 0.7rem;
  }

  .confidence-badge-modern {
    padding: 0.4rem 0.875rem;
    font-size: 0.85rem;

    svg {
      width: 16px;
      height: 16px;
    }
  }

  .assessment-link {
    padding: 0.4rem 0.875rem;
    font-size: 0.8rem;

    svg {
      width: 14px;
      height: 14px;
    }
  }

  .links-section,
  .consensus-section,
  .additional-info-section,
  .key-decisions-section {
    padding: 0.875rem;
  }

  .section-title {
    font-size: 0.95rem;

    svg {
      width: 18px;
      height: 18px;
    }
  }

  .links-header {
    font-size: 0.9rem;

    svg {
      width: 16px;
      height: 16px;
    }
  }

  .link-item {
    padding: 0.5rem 0.75rem;
    font-size: 0.85rem;

    svg {
      width: 12px;
      height: 12px;
    }
  }

  .consensus-label {
    font-size: 0.875rem;
  }

  .info-list li,
  .decisions-list li {
    font-size: 0.875rem;
  }

  .decisions-list {
    max-height: 180px;
  }
}
</style>